import { AloftPriceJSON } from '@aloft/shared-objects';
import { AloftPrice, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCalculatedMarketAdjustment,
  selectCalculatedMarketAdjustments,
} from './calculatedMarketAdjustmentSelectors/selectCalculatedMarketAdjustment.ts';
import { selectOpinionOfSiteValue } from './opinionOfSiteValueSelectors.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectUnadjustedSiteCost, selectUnadjustedSiteCosts } from './selectUnadjustedSiteCost.ts';

type SiteValueParams = {
  opinionOfSiteValue: AloftPriceJSON;
  unadjustedSiteCost: ReturnType<typeof selectUnadjustedSiteCost>;
  marketAdjustment: AloftPrice | undefined;
};

const calculateSiteValueOuter = (
  propID: string,
  { opinionOfSiteValue, unadjustedSiteCost, marketAdjustment }: SiteValueParams,
) => {
  if (propID === 'subject' || propID === 'subjectArv') {
    return AloftPrice.schema.parse(opinionOfSiteValue);
  }

  return calculateCompSiteValue(unadjustedSiteCost, marketAdjustment);
};

const calculateCompSiteValue = (
  unadjustedSiteCost: ReturnType<typeof selectUnadjustedSiteCost>,
  marketAdjustment: AloftPrice | undefined,
): AloftPrice | undefined => {
  if (isUndefined(unadjustedSiteCost) || isUndefined(marketAdjustment)) {
    return undefined;
  }

  return unadjustedSiteCost.add(marketAdjustment);
};

export const selectSiteValue = createSelector(
  [
    selectUnadjustedSiteCost,
    selectCalculatedMarketAdjustment,
    selectOpinionOfSiteValue,
    (_state, id: string) => id,
  ],
  (unadjustedSiteCost, marketAdjustment, opinionOfSiteValue, id): AloftPrice | undefined =>
    calculateSiteValueOuter(id, {
      opinionOfSiteValue,
      unadjustedSiteCost,
      marketAdjustment,
    }),
);

export const selectSiteValues = createSelector(
  [
    selectUnadjustedSiteCosts,
    selectCalculatedMarketAdjustments,
    selectOpinionOfSiteValue,
    selectCompSequence,
  ],
  (unadjustedSiteCosts, marketAdjustments, opinionOfSiteValue, compSequence) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateSiteValueOuter(propID, {
          opinionOfSiteValue,
          unadjustedSiteCost: unadjustedSiteCosts?.[propID],
          marketAdjustment: marketAdjustments?.[propID],
        }),
      ]),
    );
  },
);

export const selectSiteValueDisplay = createSelector([selectSiteValue], (siteValue) => {
  if (isUndefined(siteValue)) {
    return undefined;
  }

  return siteValue.toString(true);
});

export const selectSiteValuesDisplay = createSelector([selectSiteValues], (siteValues) => {
  if (isUndefined(siteValues)) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(siteValues).map(([propID, siteValue]) => [propID, siteValue?.toString(true)]),
  );
});
